import React, { useCallback, useContext, useEffect, useState } from "react"
import AdHocCommands from "../../api/adHocCommands"
import XMPPError from "@xmpp/error"
import { XmppContext } from "../../stream/xmppClient"
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "../../reducers/hooks"
import {
  fetchRoster,
  handleInvitationResponse,
  parseAvailableDriverShares,
  parseCurrentDriverShare,
  parseDriverAndMucJids,
  waitForMucInvitation,
} from "./utils"
import CountryCodeSelector, { countries } from "./CountryCodeSelector"
import { updateDriverProfile } from "../../reducers/chatsSliceThunks"
import { selectAccountChatByJid } from "../../reducers/accountChatsSlice"
import { findProfile } from "../../reducers/profilesSliceSelectors"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Trash } from "lucide-react"
import { languages } from "@/lib/languages"
import { useTranslation } from "react-i18next"

interface AddEditDriverModalProps {
  isOpen: boolean
  onClose: (event?: any) => void
  phoneNumber?: string
  firstName?: string
  lastName?: string
  plates?: string
  share?: string
  onActiveChatChange?: (chatJid: string) => void
  userJID?: string
  routeJid?: string
  preferredLanguage?: string
  isDoubleCrew?: boolean
}

type Share = {
  label: string
  value: string
}

export const AddEditDriverModal: React.FC<AddEditDriverModalProps> = ({
  isOpen,
  onClose,
  userJID,
  onActiveChatChange,
  routeJid,
  isDoubleCrew = false,
  ...rest
}) => {
  const { t } = useTranslation()
  const noShare: Share = {
    label: t("fleet.form.selectCompany"),
    value: "no-share",
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFetchingAvailableDriverShares, setIsFetchingAvailableDriverShares] =
    useState(false)
  const [availableShares, setAvailableShares] = useState<Share[] | undefined>(
    undefined,
  )
  const [selectedShare, setSelectedShare] = useState<string>(
    rest.share ?? noShare.value,
  )
  const [phoneNumberWithoutCode, setPhoneNumberWithoutCode] = useState("")
  const [selectedDialCode, setSelectedDialCode] = useState("+48") // Default to Poland
  const [firstName, setFirstName] = useState(rest.firstName ?? "")
  const [lastName, setLastName] = useState(rest.lastName ?? "")
  const [plates, setPlates] = useState(rest.plates ?? "")

  const dispatch = useAppDispatch()

  const store = useAppStore()
  const accountChat = useAppSelector(
    routeJid ? selectAccountChatByJid(routeJid) : () => undefined,
  )
  const driverProfile = useAppSelector(findProfile(accountChat?.driverA))

  const [preferredLanguage, setPreferredLanguage] = useState(
    rest.preferredLanguage ?? driverProfile?.preferredLanguage ?? "pl",
  )

  const isEdit = routeJid !== undefined
  const showDriverFields = !isDoubleCrew

  const { client } = useContext(XmppContext)

  useEffect(() => {
    if (rest.phoneNumber) {
      // Find matching country code
      const matchingCountry = countries.find((country) =>
        rest.phoneNumber?.startsWith(country.dialCode),
      )

      if (matchingCountry) {
        setSelectedDialCode(matchingCountry.dialCode)
        setPhoneNumberWithoutCode(
          rest.phoneNumber.substring(matchingCountry.dialCode.length),
        )
      } else {
        setPhoneNumberWithoutCode(rest.phoneNumber)
      }
    } else {
      setPhoneNumberWithoutCode("")
    }
    setFirstName(rest.firstName ?? "")
    setLastName(rest.lastName ?? "")
    setPlates(rest.plates ?? "")
    setPreferredLanguage(
      rest.preferredLanguage ?? driverProfile?.preferredLanguage ?? "pl",
    )
  }, [
    rest.phoneNumber,
    rest.firstName,
    rest.lastName,
    rest.plates,
    rest.preferredLanguage,
    driverProfile?.preferredLanguage,
  ])

  useEffect(() => {
    const getAvailableShares = async () => {
      if (isOpen) {
        setIsFetchingAvailableDriverShares(true)
        try {
          const availableSharesResult =
            await AdHocCommands.getAvailableDriverShares({
              client,
            })
          const availableShares = parseAvailableDriverShares(
            availableSharesResult,
          )

          if (userJID) {
            const currentDriverShareResult =
              await AdHocCommands.getCurrentShareDriver({
                client,
                driverJid: userJID,
              })

            const currentDriverShare = parseCurrentDriverShare(
              currentDriverShareResult,
            )

            setSelectedShare(currentDriverShare || noShare.value)
          }

          setAvailableShares(availableShares as Share[])
        } catch (error) {
          console.error("Failed to fetch available shares:", error)
          setAvailableShares([])
        } finally {
          setIsFetchingAvailableDriverShares(false)
        }
      }
    }
    getAvailableShares()
  }, [client, isOpen, noShare.value, userJID])

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true)
    e.preventDefault()

    try {
      if (showDriverFields) {
        await handleSubmitRegularRoom(e)
      } else {
        await handleSubmitNewRoom()
      }
      handleClose()
    } catch (e) {
      const error = (e as XMPPError).text

      window.appToaster.show({
        message: error,
        intent: "danger",
        timeout: 2000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitNewRoom = async () => {
    if (accountChat?.driverA) {
      await AdHocCommands.setPreferredLanguage({
        client,
        driverJid: accountChat.driverA,
        language: preferredLanguage,
      })
    }
  }

  const handleSubmitRegularRoom = async (e: React.FormEvent) => {
    const fullPhoneNumber = `${selectedDialCode}${phoneNumberWithoutCode}`

    const payload = {
      client,
      userJID,
      phoneNumber: fullPhoneNumber,
      firstName,
      lastName,
      plates,
    }

    const eventPayload = {
      phoneNumber: fullPhoneNumber,
      firstName,
      lastName,
      plates,
    }

    window.analytics.track("UpsertUser", eventPayload)

    try {
      // Promise to wait for multi-user chat (MUC) invitation after driver is added
      const invitationPromise = waitForMucInvitation(client)

      // Send command to add new driver with provided details
      const response = await AdHocCommands.addDriver(payload)

      const { driverJID, mucJID } = parseDriverAndMucJids(response)

      await fetchRoster(client, store)

      // Wait for MUC invitation and accept it

      if (mucJID && !isEdit) {
        await handleInvitationResponse(
          invitationPromise,
          client,
          store,
          mucJID,
          onActiveChatChange,
        )
      }

      if (driverJID && selectedShare && selectedShare !== "no-share") {
        await AdHocCommands.shareDriver(client, driverJID, selectedShare)
      }

      if (driverJID && isEdit) {
        dispatch(updateDriverProfile(client, driverJID))
      }

      // Set preferred language for driver
      if (driverJID) {
        await AdHocCommands.setPreferredLanguage({
          client,
          driverJid: driverJID,
          language: preferredLanguage,
        })
      }

      handleClose()
    } catch (e) {
      const error = (e as XMPPError).text

      window.appToaster.show({
        message: error,
        intent: "danger",
        timeout: 2000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePhoneNumberChange = (value: string) => {
    // Remove any + signs and spaces from input
    const cleanValue = value.replace(/[\s+]/g, "")
    setPhoneNumberWithoutCode(cleanValue)
  }

  const handleClose = useCallback(() => {
    if (!userJID) {
      setPhoneNumberWithoutCode("")
      setSelectedDialCode("+48")
      setFirstName("")
      setLastName("")
      setPlates("")
      setPreferredLanguage("pl")
    }
    onClose()
  }, [onClose, userJID])

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="font-normal sm:max-w-[480px]"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {isEdit
              ? t("fleet.driverForm.titleEdit")
              : t("fleet.driverForm.titleAdd")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            {showDriverFields && (
              <div className="space-y-2">
                <Label htmlFor="first_name">{t("fleet.form.firstName")}</Label>
                <Input
                  id="first_name"
                  disabled={isSubmitting || isFetchingAvailableDriverShares}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full"
                />
              </div>
            )}

            {showDriverFields && (
              <div className="space-y-2">
                <Label htmlFor="last_name">{t("fleet.form.lastName")}</Label>
                <Input
                  id="last_name"
                  disabled={isSubmitting || isFetchingAvailableDriverShares}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full"
                />
              </div>
            )}

            {showDriverFields && (
              <div className="space-y-2">
                <Label htmlFor="phone">{t("fleet.form.phoneNumber")}</Label>
                <div className="flex gap-2">
                  <CountryCodeSelector
                    dialCode={selectedDialCode}
                    onChange={(country) =>
                      setSelectedDialCode(country.dialCode)
                    }
                    disabled={isSubmitting || isFetchingAvailableDriverShares}
                  />
                  <Input
                    id="phone"
                    disabled={isSubmitting || isFetchingAvailableDriverShares}
                    required
                    value={phoneNumberWithoutCode}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            )}

            {showDriverFields && (
              <div className="space-y-2">
                <Label htmlFor="plates">{t("fleet.driverForm.plates")}</Label>
                <Input
                  id="plates"
                  disabled={isSubmitting || isFetchingAvailableDriverShares}
                  required
                  value={plates}
                  onChange={(e) => setPlates(e.target.value)}
                  className="w-full"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("fleet.form.preferredLanguage")}</Label>
              <Select
                disabled={isSubmitting || isFetchingAvailableDriverShares}
                value={preferredLanguage}
                onValueChange={setPreferredLanguage}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("fleet.form.selectLanguage")} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      <span className="flex items-center gap-2">
                        <span>{language.flag}</span>
                        <span>{language.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showDriverFields &&
              availableShares &&
              availableShares.length > 0 && (
                <div className="space-y-3">
                  <Label>{t("fleet.driverForm.shareTo")}</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      disabled={isSubmitting || isFetchingAvailableDriverShares}
                      value={selectedShare}
                      onValueChange={(value) => setSelectedShare(value)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue
                          placeholder={t("fleet.form.selectCompany")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={noShare.value}>
                          {noShare.label}
                        </SelectItem>
                        {availableShares.map((share) => (
                          <SelectItem key={share.value} value={share.value}>
                            {share.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={
                        !selectedShare ||
                        selectedShare === "no-share" ||
                        isFetchingAvailableDriverShares
                      }
                      onClick={(e) => {
                        e.preventDefault()
                        setSelectedShare(noShare.value)
                      }}
                      className="shrink-0"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
          </div>

          <DialogFooter>
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting || isFetchingAvailableDriverShares}
                onClick={handleClose}
              >
                {t("common.cancel")}
              </Button>
              <Button
                autoFocus
                type="submit"
                disabled={isSubmitting || isFetchingAvailableDriverShares}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting || isFetchingAvailableDriverShares
                  ? t("fleet.form.saving")
                  : t("common.save")}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
